import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Text } from '@blueprintjs/core'
import cn from 'classnames'
import { useFormik } from 'formik'
import { ClientLocaleNS } from 'lib/constants/locales'
import { ORDER_FORM_SCHEMA } from 'lib/constants/validation'
import axios from 'axios'

import { FormField, TFormFieldProps } from './components/FormField/FormField'
import { useShoppingCartController } from 'client/controllers/cartController'
import { Trans } from 'client/components/Trans/Trans'
import { Link } from 'client/components/Trans/components/Link/Link'
import { Button } from 'client/components/Button/Button'
import { router, usePage } from '@inertiajs/react'

type TProps = {}

type TPageProps = {
  order_errors?: any
}

type TOrderForm = Omit<Data.Order, 'id' | 'orderables' | 'created_at'>
type TOrderFormFieldKey = keyof TOrderForm
type TSubmitResult = {
  text: string
  status: 'success' | 'failure'
} | null

const INITIAL_VALUES: TOrderForm = {
  first_name: '',
  last_name: '',
  email_address: '',
  country: '',
  city: '',
  address: '',
  postal_code: '',
}

const OrderForm: FC<TProps> = () => {
  const {
    props: { order_errors },
  } = usePage<TPageProps>()
  const { t } = useTranslation(ClientLocaleNS.CheckoutPage)
  const { orderables, setLoadingState } = useShoppingCartController()
  const [submitResult, setSubmitResult] = useState<TSubmitResult>(null)

  const { handleChange, handleSubmit, errors, values, setErrors } = useFormik<TOrderForm>({
    initialValues: INITIAL_VALUES,
    initialErrors: order_errors ?? {},
    validationSchema: ORDER_FORM_SCHEMA,
    validateOnChange: true,
    validateOnBlur: false,
    onSubmit: (values, { validateForm }) => {
      validateForm(values).then(() => {
        axios
          .post<{ body: { redirect_url: string } }>('/api/v1/payment', {
            order: {
              ...values,
              orderables_attributes: orderables.map((item) => ({
                product_id: item.product.id,
                quantity: item.quantity,
                original_price: item.product.price,
              })),
            },
          })
          .then(({ data: { body } }) => {
            if (body?.redirect_url) {
              window.location.replace(body.redirect_url)
            }
          })
          .catch(({ message, response }) => {
            console.error(message)

            if (response?.data?.order_errors) {
              setErrors(response.data.order_errors)
            }
          })
      })
    },
  })

  useEffect(() => {
    if (order_errors) {
      setErrors(order_errors)
    }
  }, [order_errors])

  const getFormFieldProps = (key: TOrderFormFieldKey): TFormFieldProps => ({
    label: t(`form.fields.${key}.title`),
    name: key,
    id: key,
    type: 'text',
    value: values[key],
    onChange: handleChange,
    error: errors[key],
  })

  return (
    <div className="bp5-dark relative flex flex-col gap-12 px-0 py-12">
      <form className="flex w-full flex-col" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-12">
          <div className="flex flex-col w-full gap-2">
            <Text tagName="span" className="font-body---20px">
              {t('contact_information.title')}
            </Text>
            <Text tagName="span" className="font-body---16px">
              {t('contact_information.caption')}
            </Text>
          </div>
          <FormField {...getFormFieldProps('email_address')} />
          <div className="flex flex-col w-full gap-2">
            <Text tagName="span" className="font-body---20px">
              {t('billing_address.title')}
            </Text>
            <Text tagName="span" className="font-body---16px">
              {t('billing_address.caption')}
            </Text>
          </div>
          <FormField {...getFormFieldProps('country')} />
          <div className="flex gap-6 items-center">
            <FormField {...getFormFieldProps('first_name')} />
            <FormField {...getFormFieldProps('last_name')} />
          </div>
          <FormField {...getFormFieldProps('address')} />
          <div className="flex gap-6 items-center">
            <FormField {...getFormFieldProps('city')} />
            <FormField {...getFormFieldProps('postal_code')} />
          </div>
          <div className="flex w-full flex-1 flex-col items-center lg:items-start">
            <div className="my-4 flex w-full flex-1 items-center justify-center lg:justify-start">
              <Text
                tagName="span"
                className={cn(
                  'font-primary-regular---15pt flex transition-opacity',
                  !!submitResult ? 'opacity-100' : 'opacity-0',
                  {
                    'text-green-500': submitResult?.status === 'success',
                    'text-palette-red': submitResult?.status === 'failure',
                  },
                )}
              >
                {submitResult?.text ?? ''}
              </Text>
            </div>
            <div className="flex flex-col w-full gap-4">
              <Text tagName="p" className="font-text---16px">
                <Trans
                  i18nKey="form.agreement"
                  ns={ClientLocaleNS.CheckoutPage}
                  components={{ customLink: <Link /> }}
                />
              </Text>
              <Button type="submit" className="h-12">
                <Text tagName="span">{t('form.button')}</Text>
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}

export { OrderForm }
